import React, { useMemo } from 'react';
import {
  SpanRenderer,
  TraceSpan,
  ZipkinTraceChart,
  convertRunDataToTraceSpans,
  type RunData,
} from '../../components/zipkin-trace-chart';
import type { StackFrameType } from '@cotera/api';

// Re-export RunData for external use
export type { RunData };

export interface RunTraceChartProps {
  /** Flat array of frames to display in the trace chart */
  runData: StackFrameType[];
  /** Additional CSS classes */
  className?: string;
  /** Height of the chart in pixels */
  height?: number;
  /** Whether to show the timeline header */
  showTimeline?: boolean;
  /** Whether to show duration labels on spans */
  showDuration?: boolean;
  /** Whether to show the sidebar with span details */
  showSidebar?: boolean;
  /** Whether to enable navigation (clickable spans that link to run details) */
  enableNavigation?: boolean;
  /** Whether to auto-scroll to the bottom of the chart when the spans change */
  autoScroll?: boolean;
  /** Callback when a span is clicked */
  onSpanClick?: (spanId: string) => void;
}

/**
 * A reusable trace chart component for displaying run execution timelines.
 *
 * Features:
 * - Displays hierarchical run execution with timing information
 * - Optional clickable spans for navigation to run details
 * - Configurable timeline, duration labels, and sidebar
 * - Built on top of the modular ZipkinTraceChart components
 *
 * @example
 * ```tsx
 * <RunTraceChart
 *   runData={runData}
 *   height={400}
 *   enableNavigation={true}
 *   showTimeline={true}
 *   showSidebar={true}
 * />
 * ```
 */
export const RunTraceChart: React.FC<RunTraceChartProps> = ({
  runData,
  className,
  height = 400,
  showTimeline = true,
  showDuration = true,
  showSidebar = true,
  enableNavigation = true,
  autoScroll = false,
  onSpanClick,
}) => {
  // Convert run data to trace spans
  const traceSpans = useMemo(
    () => convertRunDataToTraceSpans(runData),
    [runData]
  );

  // Custom renderer that makes spans clickable links
  const renderSpanAsLink: SpanRenderer<RunData> = (
    spanData,
    index,
    timeRange
  ) => {
    if (!enableNavigation) {
      // If navigation is disabled, just render the default span
      return (
        <TraceSpan
          spanData={spanData}
          index={index}
          timeRange={timeRange}
          showDuration={showDuration}
          isHovered={false}
          onHover={() => {}}
        />
      );
    }

    // Navigation within frames is disabled since we removed frameKey concept
    // Just render the span without navigation
    return (
      <TraceSpan
        spanData={spanData}
        index={index}
        timeRange={timeRange}
        showDuration={showDuration}
        isHovered={false}
        onHover={() => {}}
      />
    );
  };

  return (
    <ZipkinTraceChart
      autoScroll={autoScroll}
      spans={traceSpans}
      height={height}
      showTimeline={showTimeline}
      showDuration={showDuration}
      showSidebar={showSidebar}
      className={className}
      renderSpan={renderSpanAsLink}
      onSpanClick={onSpanClick}
    />
  );
};
